import api from './axios';
import {
  LoginCredentials,
  ChangePasswordData,
  CreateUserData,
  Category,
  Evaluation,
  EvaluationType,
  ReviewAssignment,
  Project,
  Task,
  ProjectComment,
  TaskComment,
  ExternalReviewRequest,
  ExternalReviewSubmission,
  Organisation,
  OrgSettings,
  OrgPlan,
  Feature,
} from '../types';


export const authAPI = {
  login: (credentials: LoginCredentials) => api.post('/auth/login', credentials),
  changePassword: (data: ChangePasswordData) => api.post('/auth/change-password', data),
  getProfile: () => api.get('/auth/profile'),
  selectOrg: (orgId: string) => api.post('/auth/select-org', { orgId }),
};

// User APIs
export const userAPI = {
  createUser: (data: CreateUserData) => api.post('/users', data),
  getAllUsers: () => api.get('/users'),
  getUserById: (id: string) => api.get(`/users/${id}`),
  updateUser: (id: string, data: {
    name?: string;
    levelId?: string;
    organizationRoleId?: string;
    primaryDepartmentId?: string | null;
    isActive?: boolean;
  }) => api.put(`/users/${id}`, data),
  updateUserLevel: (id: string, level: string) => api.put(`/users/${id}/level`, { level }),
  resetPassword: (id: string, newPassword: string) =>
    api.put(`/users/${id}/reset-password`, { newPassword }),
  requestPasswordReset: (email: string) => api.post('/users/reset-password', { email }),
  deleteUser: (id: string) => api.delete(`/users/${id}`),
};

// Category APIs
export const categoryAPI = {
  createCategory: (data: Partial<Category>) => api.post('/categories', data),
  createBulkCategories: (data: {
    categories: Array<{
      name: string;
      weight: number;
      subCategories: Array<{ name: string; weight: number }>;
    }>;
    levelGroup: string;
    evaluationTypeId: string;
  }) => api.post('/categories/bulk', data),
  getAllCategories: () => api.get('/categories'),
  getCategoriesByLevelGroup: (levelGroup: string, evaluationTypeId?: string) =>
    api.get(`/categories/level-group/${levelGroup}`, { params: evaluationTypeId ? { evaluationTypeId } : undefined }),
  validateCategoriesTotal: (levelGroup: string) =>
    api.get(`/categories/validate/${levelGroup}`),
  updateCategory: (id: string, data: Partial<Category>) => api.put(`/categories/${id}`, data),
  deleteCategory: (id: string) => api.delete(`/categories/${id}`),
  getCatalog: () => api.get('/categories/catalog'),
};

// Evaluation APIs
export const evaluationAPI = {
  createOrUpdateEvaluation: (data: Partial<Evaluation>) => api.post('/evaluations', data),
  submitEvaluation: (id: string) => api.put(`/evaluations/${id}/submit`),
  adminUpdateEvaluation: (id: string, data: Partial<Evaluation>) =>
    api.put(`/evaluations/${id}/admin-update`, data),
  updateAdminScores: (id: string, data: Partial<Evaluation>) =>
    api.put(`/evaluations/${id}/admin-scores`, data),
  getMyEvaluations: () => api.get('/evaluations/my-evaluations'),
  getActiveEvaluation: (evaluationPeriod?: string, evaluationTypeId?: string, assignmentId?: string) =>
    api.get('/evaluations/active', { params: { evaluationPeriod, evaluationTypeId, assignmentId } }),
  getAllEvaluationPeriods: () => api.get('/evaluations/periods'),
  getAllEvaluations: (params?: { evaluationPeriod?: string; evaluationTypeId?: string; status?: string; sortBy?: string; sortOrder?: string }) =>
    api.get('/evaluations/all', { params }),
  getEvaluationById: (id: string) => api.get(`/evaluations/${id}`),
  exportToExcel: (evaluationPeriod?: string) =>
    api.get('/evaluations/export', {
      responseType: 'blob',
      params: { evaluationPeriod }
    }),
};

// Evaluation Type APIs
export const evaluationTypeAPI = {
  createEvaluationType: (data: Partial<EvaluationType>) => api.post('/evaluation-types', data),
  getAllEvaluationTypes: (isActive?: boolean) =>
    api.get('/evaluation-types', { params: { isActive } }),
  getEvaluationTypeById: (id: string) => api.get(`/evaluation-types/${id}`),
  updateEvaluationType: (id: string, data: Partial<EvaluationType>) =>
    api.put(`/evaluation-types/${id}`, data),
  deleteEvaluationType: (id: string) => api.delete(`/evaluation-types/${id}`),
  getCategoriesByEvaluationType: (id: string, levelGroup?: string) =>
    api.get(`/evaluation-types/${id}/categories`, { params: { levelGroup } }),
};

// Review Assignment APIs
export const reviewAssignmentAPI = {
  createReviewAssignment: (data: Partial<ReviewAssignment>) =>
    api.post('/review-assignments', data),
  getAllReviewAssignments: (status?: string, evaluationTypeId?: string) =>
    api.get('/review-assignments', { params: { status, evaluationTypeId } }),
  getUserReviewAssignments: () => api.get('/review-assignments/my-assignments'),
  closeReviewAssignment: (id: string) => api.put(`/review-assignments/${id}/close`),
  reopenReviewAssignment: (id: string) => api.put(`/review-assignments/${id}/reopen`),
  getReviewAssignmentStats: (id: string) => api.get(`/review-assignments/${id}/stats`),
};

// Project APIs
export const projectAPI = {
  // Projects
  getAllProjects: (params?: { status?: string; visibility?: string; category?: string; tag?: string; rootOnly?: string }) =>
    api.get('/projects', { params }),
  getMyProjects: () => api.get('/projects/my-projects'),
  getProjectUsers: () => api.get('/projects/users/all'),
  getAvailableParentProjects: () => api.get('/projects/available-parents'),
  getProjectById: (id: string) => api.get(`/projects/${id}`),
  getChildProjects: (id: string) => api.get(`/projects/${id}/children`),
  getProjectsByApplication: (applicationId: string) => api.get(`/projects/application/${applicationId}`),
  createProject: (data: Partial<Project>) => api.post('/projects', data),
  updateProject: (id: string, data: Partial<Project>) => api.put(`/projects/${id}`, data),
  deleteProject: (id: string) => api.delete(`/projects/${id}`),
  submitForApproval: (id: string) => api.post(`/projects/${id}/submit-for-approval`),

  // Members
  addMember: (id: string, userId: string, role?: string) =>
    api.post(`/projects/${id}/members`, { userId, role }),
  removeMember: (id: string, memberId: string) =>
    api.delete(`/projects/${id}/members/${memberId}`),
  joinAsTester: (id: string) =>
    api.post(`/projects/${id}/join-as-tester`),

  // Admin Review
  addAdminReview: (id: string, data: { score?: number; feedback?: string; approved?: boolean }) =>
    api.post(`/projects/${id}/review`, data),

  // Comments
  getComments: (id: string) => api.get(`/projects/${id}/comments`),
  addComment: (id: string, data: Partial<ProjectComment>) =>
    api.post(`/projects/${id}/comments`, data),

  // Tasks
  getTasks: (id: string, status?: string) =>
    api.get(`/projects/${id}/tasks`, { params: { status } }),
  
  // Project Approval Endpoints
  approveProject: (id: string, data: { comments?: string; reason?: string }) =>
    api.put(`/admin/projects/${id}/approve`, data),
  rejectProject: (id: string, data: { comments?: string; reason?: string }) =>
    api.put(`/admin/projects/${id}/reject`, data),
  getPendingApprovals: () => api.get('/admin/pending-approvals'),
  getProjectApprovalDetails: (id: string) => api.get(`/admin/projects/${id}/approval-details`),
  
  // Admin Dashboard Endpoints  
  getAvailableDepartmentsForMetrics: () => api.get('/admin/departments/available'),
  getUsersWithProjectMetrics: (params?: { year?: number; startDate?: string; endDate?: string; status?: string; departmentId?: string }) => 
    api.get('/admin/users-with-metrics', { params }),
  getUserProjectDetails: (userId: string, params?: { year?: number; startDate?: string; endDate?: string }) =>
    api.get(`/admin/users/${userId}/project-details`, { params }),
  getProjectManagementReport: (params?: { departmentId?: string }) =>
    api.get('/admin/report/project-management', { params }),
  getWorkloadAnalysis: (params?: { departmentId?: string }) =>
    api.get('/admin/workload-analysis', { params }),
  // Accessible to all authenticated users (no admin gate)
  getProjectBoardData: (params?: { departmentId?: string }) =>
    api.get('/projects/board-data', { params }),

  // Pause / Resume
  pause: (id: string, data: { reason?: string; pauseScope?: string }) =>
    api.post(`/projects/${id}/pause`, data),
  resume: (id: string, data: { deadlinesAdjusted?: boolean; adjustmentNotes?: string }) =>
    api.post(`/projects/${id}/resume`, data),

  // Backlog
  getBacklog: (id: string) => api.get(`/projects/${id}/backlog`),
};

// Task APIs
export const taskAPI = {
  // Tasks
  getAllTasks: (params?: { status?: string; projectId?: string; priority?: string }) =>
    api.get('/tasks', { params }),
  getMyTasks: () => api.get('/tasks/my-tasks'),
  getTaskById: (id: string) => api.get(`/tasks/${id}`),
  createTask: (data: Partial<Task>) => api.post('/tasks', data),
  updateTask: (id: string, data: Partial<Task>) => api.put(`/tasks/${id}`, data),
  deleteTask: (id: string) => api.delete(`/tasks/${id}`),

  // Assignment
  assignTask: (id: string, userId: string) => api.post(`/tasks/${id}/assign`, { userId }),
  unassignTask: (id: string, userId: string) => api.post(`/tasks/${id}/unassign`, { userId }),

  // Subtasks
  createSubtask: (id: string, title: string) => api.post(`/tasks/${id}/subtasks`, { title }),
  updateSubtask: (id: string, subtaskId: string, completed: boolean) =>
    api.put(`/tasks/${id}/subtasks/${subtaskId}`, { completed }),
  deleteSubtask: (id: string, subtaskId: string) => api.delete(`/tasks/${id}/subtasks/${subtaskId}`),

  // Attachments
  addAttachment: (id: string, data: { name: string; url: string; type: string }) =>
    api.post(`/tasks/${id}/attachments`, data),
  deleteAttachment: (id: string, url: string) =>
    api.delete(`/tasks/${id}/attachments`, { data: { url } }),

  // Comments
  getComments: (id: string) => api.get(`/tasks/${id}/comments`),
  addComment: (id: string, data: Partial<TaskComment>) => api.post(`/tasks/${id}/comments`, data),

  // Status
  updateStatus: (id: string, status: string) => api.put(`/tasks/${id}/status`, { status }),

  // Blockers
  addBlocker: (id: string, data: { type: string; refId?: string; description: string }) =>
    api.post(`/tasks/${id}/blockers`, data),
  resolveBlocker: (id: string, blockerIndex: number) =>
    api.put(`/tasks/${id}/blockers/${blockerIndex}/resolve`),

  // Subtask attachments
  addSubtaskAttachment: (id: string, subtaskId: string, data: { name: string; url: string; type: string }) =>
    api.post(`/tasks/${id}/subtasks/${subtaskId}/attachments`, data),
  deleteSubtaskAttachment: (id: string, subtaskId: string, url: string) =>
    api.delete(`/tasks/${id}/subtasks/${subtaskId}/attachments`, { data: { url } }),
};

// External Review APIs
export const externalReviewAPI = {
  // Request external review
  requestReview: (projectId: string, data: ExternalReviewRequest) =>
    api.post(`/projects/${projectId}/external-reviews`, data),

  // Get all external reviews for a project
  getProjectReviews: (projectId: string) => api.get(`/projects/${projectId}/external-reviews`),

  // Delete external review request
  deleteReview: (reviewId: string) => api.delete(`/external-reviews/${reviewId}`),

  // Resend review request email
  resendReview: (reviewId: string) => api.post(`/external-reviews/${reviewId}/resend`),

  getReviewByToken: (token: string) => api.get(`/external-reviews/${token}`),
  submitReview: (token: string, data: ExternalReviewSubmission) =>
    api.post(`/external-reviews/${token}/submit`, data),
};

// Department APIs
export const departmentAPI = {
  // Get all departments
  getAllDepartments: (params?: { status?: string; includeMembers?: boolean; scope?: string }) =>
    api.get('/departments', { params }),

  // Get departments user can manage (HODs)
  getMyManagedDepartments: () => api.get('/departments/my-managed'),

  // Get department by ID
  getDepartmentById: (id: string) => api.get(`/departments/${id}`),

  // Create department
  createDepartment: (data: {
    name: string;
    code: string;
    description?: string;
    parentDepartmentId?: string;
    headUserId?: string;
  }) => api.post('/departments', data),

  // Update department
  updateDepartment: (id: string, data: {
    name?: string;
    code?: string;
    description?: string;
    parentDepartmentId?: string | null;
    headUserId?: string | null;
    status?: 'active' | 'inactive';
  }) => api.put(`/departments/${id}`, data),

  // Delete department
  deleteDepartment: (id: string) => api.delete(`/departments/${id}`),

  // Get department members
  getDepartmentMembers: (id: string) => api.get(`/departments/${id}/members`),

  // Add member to department
  addDepartmentMember: (id: string, data: {
    userId: string;
    organizationRoleId?: string;
    isHead?: boolean;
    isPrimary?: boolean;
    managedDepartmentIds?: string[];
  }) => api.post(`/departments/${id}/members`, data),

  // Remove member from department
  removeDepartmentMember: (departmentId: string, userId: string) =>
    api.delete(`/departments/${departmentId}/members/${userId}`),

  // Set department head
  setDepartmentHead: (id: string, userId: string) =>
    api.put(`/departments/${id}/head`, { userId }),

  // Update managed departments for a user
  updateManagedDepartments: (userId: string, departmentId: string, managedDepartmentIds: string[]) =>
    api.put(`/departments/assignments/${userId}/managed`, { departmentId, managedDepartmentIds }),

  // Get eligible collaborating departments for a project
  getEligibleCollaboratingDepartments: (projectId: string) =>
    api.post('/departments/eligible-collaboration-departments', { projectId }),
};

// Level APIs
export const levelAPI = {
  // Get all levels
  getAllLevels: () => api.get('/levels'),

  // Get level by ID
  getLevelById: (id: string) => api.get(`/levels/${id}`),

  // Create level (admin only)
  createLevel: (data: {
    name: string;
    code: string;
    rank: number;
    description?: string;
    isDefault?: boolean;
  }) => api.post('/levels', data),

  // Update level (admin only)
  updateLevel: (id: string, data: {
    name?: string;
    code?: string;
    rank?: number;
    description?: string;
    isDefault?: boolean;
    isActive?: boolean;
  }) => api.put(`/levels/${id}`, data),

  // Delete level / deactivate (admin only)
  deleteLevel: (id: string) => api.delete(`/levels/${id}`),

  // Reorder levels (admin only)
  reorderLevels: (orders: { id: string; rank: number }[]) =>
    api.post('/levels/reorder', { orders }),
};

// Organization Role APIs
export const organizationRoleAPI = {
  // Get all roles
  getAllRoles: (includePermissions?: boolean, targetOrgId?: string) =>
    api.get('/organization-roles', { params: { includePermissions, targetOrgId } }),

  // Get role by ID
  getRoleById: (id: string) => api.get(`/organization-roles/${id}`),

  // Create role
  createRole: (data: {
    name: string;
    code: string;
    description?: string;
    priority?: number;
    permissionIds?: string[];
    canApproveProjects?: boolean;
    canManageUsers?: boolean;
    canManageDepartments?: boolean;
    canViewAllProjects?: boolean;
    canViewAllEvaluations?: boolean;
  }) => api.post('/organization-roles', data),

  // Update role
  updateRole: (id: string, data: {
    name?: string;
    code?: string;
    description?: string;
    priority?: number;
    permissionIds?: string[];
    canApproveProjects?: boolean;
    canManageUsers?: boolean;
    canManageDepartments?: boolean;
    canViewAllProjects?: boolean;
    canViewAllEvaluations?: boolean;
    canCreatePublicKnowledgeBase?: boolean;
    canManageOrganisations?: boolean;
    isActive?: boolean;
  }) => api.put(`/organization-roles/${id}`, data),

  // Delete role
  deleteRole: (id: string) => api.delete(`/organization-roles/${id}`),

  // Get all permissions
  getAllPermissions: () => api.get('/organization-roles/permissions'),

  // Assign role to user
  assignRoleToUser: (data: {
    userId: string;
    roleId: string;
    departmentId?: string;
  }) => api.put('/organization-roles/assign', data),

  // Get users by role
  getUsersByRole: (roleId: string) => api.get(`/organization-roles/${roleId}/users`),

  // Get all users with roles and departments
  getAllUsersWithRoles: () => api.get('/organization-roles/users/all'),
};

// Approval APIs
export const approvalAPI = {
  // Request collaboration from another department
  requestCollaboration: (projectId: string, data: {
    targetDepartmentId: string;
    approvalType: 'collaboration' | 'cross_department' | 'high_authority' | 'general';
    requestReason?: string;
    urgency?: 'low' | 'medium' | 'high' | 'critical';
  }) => api.post(`/approvals/projects/${projectId}/request-collaboration`, data),

  // Get my approval requests (pending, approved, rejected)
  getMyApprovals: () => api.get('/approvals/my-approvals'),

  // Get all approvals for a project
  getProjectApprovals: (projectId: string) => api.get(`/approvals/projects/${projectId}/approvals`),

  // Approve a collaboration request
  approveCollaboration: (approvalId: string, comments?: string) =>
    api.post(`/approvals/approvals/${approvalId}/approve`, { comments }),

  // Reject a collaboration request
  rejectCollaboration: (approvalId: string, comments?: string) =>
    api.post(`/approvals/approvals/${approvalId}/reject`, { comments }),

  // Escalate to higher authority
  escalateToHigherAuthority: (approvalId: string, data: {
    escalationReason: string;
    escalateTo?: string;
  }) => api.post(`/approvals/approvals/${approvalId}/escalate`, data),

  // Get approval by shareable token
  getApprovalByToken: (token: string) => api.get(`/approvals/approvals/token/${token}`),

  // Approve via shareable token
  approveViaToken: (token: string, comments?: string) =>
    api.post(`/approvals/approvals/token/${token}/approve`, { comments }),
};

// Product APIs
export const productAPI = {
  // Get all products
  getAllProducts: (params?: { page?: number; limit?: number; status?: string; visibility?: string }) =>
    api.get('/products', { params }),

  // Get products with applications
  getProductsWithApplications: (params?: { page?: number; limit?: number }) =>
    api.get('/products/with-applications', { params }),

  // Get product by ID
  getProductById: (id: string, includeApplications?: boolean) =>
    api.get(`/products/${id}`, { params: { includeApplications } }),

  // Create product (admin only)
  createProduct: (data: {
    name: string;
    description: string;
    visibility?: 'public' | 'private';
    status?: string;
    priority?: string;
    category?: string;
    tags?: string[];
    departmentId?: string;
    visibleToDepartments?: string[];
  }) => api.post('/products', data),

  // Update product
  updateProduct: (id: string, data: {
    name?: string;
    description?: string;
    visibility?: 'public' | 'private';
    status?: string;
    priority?: string;
    category?: string;
    tags?: string[];
    visibleToDepartments?: string[];
  }) => api.put(`/products/${id}`, data),

  // Delete product
  deleteProduct: (id: string) => api.delete(`/products/${id}`),
};

// Application APIs
export const applicationAPI = {
  // Get all applications
  getAllApplications: (params?: {
    page?: number;
    limit?: number;
    applicationType?: string;
    status?: string;
    productId?: string;
    visibility?: string;
  }) => api.get('/applications', { params }),

  // Get application by ID
  getApplicationById: (id: string, includeProjects?: boolean, projectsLimit?: number) =>
    api.get(`/applications/${id}`, { params: { includeProjects, projectsLimit } }),

  // Get applications by product
  getApplicationsByProduct: (productId: string) =>
    api.get(`/applications/product/${productId}`),

  // Create application (admin only)
  createApplication: (data: {
    name: string;
    description: string;
    applicationType: 'backend' | 'frontend_web' | 'mobile_ios' | 'mobile_android' | 'desktop' | 'service' | 'library' | 'other';
    productIds: string[];
    departmentId: string;
    departmentName: string;
    collaboratingDepartments?: any[];
    visibility?: 'public' | 'private';
    visibleToDepartments?: string[];
    techStack?: string[];
    version?: string;
    links?: any[];
    status?: string;
    tags?: string[];
  }) => api.post('/applications', data),

  // Update application
  updateApplication: (id: string, data: {
    description?: string;
    applicationType?: string;
    productIds?: string[];
    collaboratingDepartments?: any[];
    visibility?: 'public' | 'private';
    visibleToDepartments?: string[];
    techStack?: string[];
    version?: string;
    links?: any[];
    status?: string;
    tags?: string[];
  }) => api.put(`/applications/${id}`, data),

  // Delete application
  deleteApplication: (id: string) => api.delete(`/applications/${id}`),

  // Add member to application
  addMember: (id: string, userId: string, role?: 'lead' | 'developer' | 'viewer') =>
    api.post(`/applications/${id}/members`, { userId, role }),

  // Remove member from application
  removeMember: (id: string, memberId: string) =>
    api.delete(`/applications/${id}/members/${memberId}`),
};

// Tag APIs
export const tagAPI = {
  // Get all accessible tags
  getAllTags: (params?: { category?: string; scope?: string; search?: string }) =>
    api.get('/tags', { params }),

  // Get popular tags
  getPopularTags: (params?: { limit?: number; category?: string }) =>
    api.get('/tags/popular', { params }),

  // Suggest tags (autocomplete)
  suggestTags: (query: string) =>
    api.get('/tags/suggest', { params: { q: query } }),

  // Search by tags
  searchByTags: (tags: string[], entityTypes?: string[]) =>
    api.get('/tags/search', { params: { tags: tags.join(','), entityTypes: entityTypes?.join(',') } }),

  // Get tag by name
  getTagByName: (name: string) =>
    api.get(`/tags/${name}`),

  // Create tag
  createTag: (data: {
    name: string;
    description?: string;
    category?: 'technology' | 'feature' | 'priority' | 'status' | 'team' | 'custom';
    color?: string;
    scope?: 'global' | 'department' | 'private';
    departmentId?: string;
  }) => api.post('/tags', data),

  // Update tag
  updateTag: (id: string, data: {
    description?: string;
    color?: string;
    category?: string;
  }) => api.put(`/tags/${id}`, data),

  // Delete tag
  deleteTag: (id: string) => api.delete(`/tags/${id}`),

  // Add entity to tag
  addEntityToTag: (tagName: string, entityType: string, entityId: string) =>
    api.post(`/tags/${tagName}/entities`, { entityType, entityId }),

  // Remove entity from tag
  removeEntityFromTag: (tagName: string, entityType: string, entityId: string) =>
    api.delete(`/tags/${tagName}/entities/${entityType}/${entityId}`),

  // Get entities by tag
  getEntitiesByTag: (tagName: string, entityType?: string) =>
    api.get(`/tags/${tagName}/entities`, { params: { entityType } }),
};

// Knowledge Base APIs
export const knowledgeBaseAPI = {
  list: async (params?: { filter?: string; search?: string; tag?: string; projectId?: string; taskId?: string }) => {
    const res = await api.get('/knowledge-base', { params });
    return res.data?.data ?? res.data;
  },
  get: async (id: string) => {
    const res = await api.get(`/knowledge-base/${id}`);
    return res.data?.data ?? res.data;
  },
  create: async (data: any) => {
    const res = await api.post('/knowledge-base', data);
    return res.data?.data ?? res.data;
  },
  update: async (id: string, data: any) => {
    const res = await api.put(`/knowledge-base/${id}`, data);
    return res.data?.data ?? res.data;
  },
  delete: (id: string) => api.delete(`/knowledge-base/${id}`),

  addCollaborator: (id: string, collaboratorId: string, collaboratorName: string) =>
    api.post(`/knowledge-base/${id}/collaborators`, { collaboratorId, collaboratorName }),
  removeCollaborator: (id: string, collaboratorId: string) =>
    api.delete(`/knowledge-base/${id}/collaborators/${collaboratorId}`),

  getComments: async (id: string) => {
    const res = await api.get(`/knowledge-base/${id}/comments`);
    return res.data?.data ?? res.data;
  },
  addComment: async (id: string, content: string, parentCommentId?: string) => {
    const res = await api.post(`/knowledge-base/${id}/comments`, { content, parentCommentId });
    return res.data?.data ?? res.data;
  },
  deleteComment: (id: string, commentId: string) =>
    api.delete(`/knowledge-base/${id}/comments/${commentId}`),

  uploadFile: async (file: File, onProgress?: (pct: number) => void) => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post('/knowledge-base/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data?.url ?? res.data;
  },

  getDocsByProject: async (projectId: string) => {
    const res = await api.get(`/knowledge-base/linked/project/${projectId}`);
    return res.data?.data ?? res.data;
  },
  getDocsByTask: async (taskId: string) => {
    const res = await api.get(`/knowledge-base/linked/task/${taskId}`);
    return res.data?.data ?? res.data;
  },
};

// Organisation APIs
export const organisationAPI = {
  getAll: (params?: { isActive?: boolean; plan?: string; page?: number; limit?: number }) =>
    api.get('/organisations', { params }),
  getById: (id: string) =>
    api.get(`/organisations/${id}`),
  create: (data: Partial<Organisation> & { adminUser?: { name: string; email: string; password: string; phone?: string } }) =>
    api.post('/organisations', data),
  update: (id: string, data: Partial<Organisation>) =>
    api.patch(`/organisations/${id}`, data),
  changePlan: (id: string, plan: OrgPlan) =>
    api.patch(`/organisations/${id}/plan`, { plan }),
  updateSettings: (id: string, settings: Partial<OrgSettings>) =>
    api.patch(`/organisations/${id}/settings`, settings),
  searchUsers: (q: string) =>
    api.get('/users/search', { params: { q } }),
  addAdmin: (id: string, userId: string) =>
    api.post(`/organisations/${id}/admins`, { userId }),
  createAdmin: (id: string, data: { name: string; email: string; password: string; phone?: string }) =>
    api.post(`/organisations/${id}/admins/new`, data),
  removeAdmin: (id: string, userId: string) =>
    api.delete(`/organisations/${id}/admins/${userId}`),
  addDomain: (id: string, domain: string, isPrimary?: boolean) =>
    api.post(`/organisations/${id}/domains`, { domain, isPrimary }),
  removeDomain: (id: string, domain: string) =>
    api.delete(`/organisations/${id}/domains/${encodeURIComponent(domain)}`),
  suspend: (id: string, reason?: string) =>
    api.patch(`/organisations/${id}/suspend`, { reason }),
  reinstate: (id: string) =>
    api.patch(`/organisations/${id}/reinstate`),
  // Per-org database
  provisionDatabase: (id: string, data: { databaseUri?: string; databaseName?: string }) =>
    api.post(`/organisations/${id}/database/provision`, data),
  getDatabaseStatus: (id: string) =>
    api.get(`/organisations/${id}/database/status`),
  evictDatabaseConnection: (id: string, disable?: boolean) =>
    api.delete(`/organisations/${id}/database/connection`, { params: disable ? { disable: 'true' } : undefined }),
  migrateToDatabase: (id: string) =>
    api.post(`/organisations/${id}/database/migrate`),
};

// Level Group APIs
export const levelGroupAPI = {
  getAllLevelGroups: () => api.get('/level-groups'),
  getLevelGroupById: (id: string) => api.get(`/level-groups/${id}`),
  createLevelGroup: (data: { name: string; code: string; description?: string; levelIds: string[]; isDefault?: boolean }) =>
    api.post('/level-groups', data),
  updateLevelGroup: (id: string, data: any) => api.put(`/level-groups/${id}`, data),
  deleteLevelGroup: (id: string) => api.delete(`/level-groups/${id}`),
};

// Membership APIs
export const membershipAPI = {
  getMyMemberships: () => api.get('/memberships/my'),
  getUserOrgs: (userId: string) => api.get(`/memberships/user/${userId}`),
  getOrgMembers: (orgId: string) => api.get(`/memberships/org/${orgId}`),
  addUserToOrg: (data: { userId: string; orgId: string; organizationRoleId?: string; isPrimary?: boolean }) =>
    api.post('/memberships', data),
  updateMembership: (membershipId: string, data: { organizationRoleId?: string; isPrimary?: boolean; status?: string }) =>
    api.put(`/memberships/${membershipId}`, data),
  removeMembership: (membershipId: string) => api.delete(`/memberships/${membershipId}`),
};

// Folder APIs
export const folderAPI = {
  getByProject: (projectId: string) => api.get(`/folders/project/${projectId}`),
  getById: (id: string) => api.get(`/folders/${id}`),
  getChildren: (id: string) => api.get(`/folders/${id}/children`),
  create: (data: {
    projectId: string;
    name: string;
    description?: string;
    parentId?: string;
    parentType?: string;
    visibility?: string;
    startDate?: string;
    estimatedEndDate?: string;
  }) => api.post('/folders', data),
  update: (id: string, data: {
    name?: string;
    description?: string;
    visibility?: string;
    startDate?: string;
    estimatedEndDate?: string;
    amendmentReason?: string;
  }) => api.put(`/folders/${id}`, data),
  delete: (id: string) => api.delete(`/folders/${id}`),
  move: (id: string, data: { newParentId: string; newParentType: string }) =>
    api.post(`/folders/${id}/move`, data),
  convertToFeature: (id: string) => api.post(`/folders/${id}/convert-to-feature`),
};

// Feature APIs
export const featureAPI = {
  getByProject: (projectId: string) => api.get(`/features/project/${projectId}`),
  getById: (id: string) => api.get(`/features/${id}`),
  create: (data: Partial<Feature>) => api.post('/features', data),
  update: (id: string, data: Partial<Feature>) => api.put(`/features/${id}`, data),
  delete: (id: string) => api.delete(`/features/${id}`),
  getTasks: (id: string) => api.get(`/features/${id}/tasks`),
  addMember: (id: string, data: { userId: string; name: string; role?: string }) =>
    api.post(`/features/${id}/members`, data),
  removeMember: (id: string, memberId: string) =>
    api.delete(`/features/${id}/members/${memberId}`),
  addBlocker: (id: string, data: { type: string; refId?: string; description: string }) =>
    api.post(`/features/${id}/blockers`, data),
  resolveBlocker: (id: string, blockerIndex: number) =>
    api.put(`/features/${id}/blockers/${blockerIndex}/resolve`),
  submitForApproval: (id: string) => api.post(`/features/${id}/submit`),
  approve: (id: string, data?: { escalateTo?: string }) => api.post(`/features/${id}/approve`, data || {}),
  reject: (id: string, data: { reason: string }) => api.post(`/features/${id}/reject`, data),
  dispute: (id: string, data: { reason: string }) => api.post(`/features/${id}/dispute`, data),
  allowManualOverride: (id: string) => api.post(`/features/${id}/allow-manual`),
  getPendingApprovals: () => api.get('/features/approvals/pending'),
  getEligibleApprovers: (id: string) => api.get(`/features/${id}/eligible-approvers`),
};

// Story Point APIs
export const storyPointAPI = {
  getMyPoints: (month: number, year: number) =>
    api.get('/story-points/my', { params: { month, year } }),
  getTeamPoints: (projectId: string, month: number, year: number) =>
    api.get(`/story-points/team/${projectId}`, { params: { month, year } }),
  getTeamTaskSummary: (projectId: string) =>
    api.get(`/story-points/team/${projectId}/summary`),
  getDepartmentBoard: () => api.get('/story-points/department-board'),
  getUserPointsProfile: (userId: string) =>
    api.get(`/story-points/user/${userId}/profile`),
  getUserYearSummary: (userId: string, year: number) =>
    api.get(`/story-points/user/${userId}/year`, { params: { year } }),
  getUserMonthlyBreakdown: (userId: string, year: number) =>
    api.get(`/story-points/user/${userId}/monthly`, { params: { year } }),
  submitMonth: (month: number, year: number) =>
    api.post(`/story-points/submit/${month}/${year}`),
  approveMonth: (userId: string, month: number, year: number) =>
    api.post(`/story-points/approve/${userId}/${month}/${year}`),
  rejectMonth: (userId: string, month: number, year: number, reason: string) =>
    api.post(`/story-points/reject/${userId}/${month}/${year}`, { reason }),
  awardPoints: (userId: string, points: number, reason: string) =>
    api.post(`/story-points/award/${userId}`, { points, reason }),
  getPendingApprovals: () => api.get('/story-points/pending-approvals'),
};

// Project Link APIs
export const projectLinkAPI = {
  create: (data: { sourceProjectId: string; targetDepartmentId?: string; targetProjectId?: string; linkType: string; description?: string }) =>
    api.post('/project-links', data),
  getByProject: (projectId: string, params?: { status?: string }) =>
    api.get(`/project-links/project/${projectId}`, { params }),
  getById: (id: string) => api.get(`/project-links/${id}`),
  getAllPending: () => api.get('/project-links/pending'),
  getPendingByDepartment: (departmentId: string) => api.get(`/project-links/department/${departmentId}/pending`),
  acceptInvite: (id: string, data: { targetProjectId: string }) => api.post(`/project-links/${id}/accept`, data),
  approve: (id: string) => api.post(`/project-links/${id}/approve`),
  reject: (id: string) => api.post(`/project-links/${id}/reject`),
  cancel: (id: string) => api.post(`/project-links/${id}/cancel`),
};

// Evaluation Template APIs
export const evaluationTemplateAPI = {
  create: (data: any) => api.post('/evaluation-templates', data),
  getAll: (params?: Record<string, string>) => api.get('/evaluation-templates', { params }),
  getById: (id: string) => api.get(`/evaluation-templates/${id}`),
  update: (id: string, data: any) => api.put(`/evaluation-templates/${id}`, data),
  delete: (id: string) => api.delete(`/evaluation-templates/${id}`),
};

// Notification APIs
export const notificationAPI = {
  getMyNotifications: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
    api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id: string) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (id: string) => api.delete(`/notifications/${id}`),
};

// AI APIs
export const aiAPI = {
  estimateStoryPoints: (featureId: string, force?: boolean) =>
    api.post('/ai/estimate-story-points', { featureId, force }),
  estimateQAPlan: (planId: string, force?: boolean) =>
    api.post('/ai/estimate-qa-plan', { planId, force }),
};

// Task Batch APIs
export const taskBatchAPI = {
  createDraft: (data: { projectId: string; name?: string; items: { itemId: string; itemType: 'task' | 'bug' }[]; preEstimatedPoints?: Record<string, number> }) =>
    api.post('/task-batches', data),
  confirmSubmit: (id: string, itemPoints?: Record<string, number>) =>
    api.post(`/task-batches/${id}/submit`, { itemPoints }),
  getByProject: (projectId: string) => api.get(`/task-batches/project/${projectId}`),
  getPending: () => api.get('/task-batches/pending'),
  approve: (id: string) => api.post(`/task-batches/${id}/approve`),
  reject: (id: string, reason: string) => api.post(`/task-batches/${id}/reject`, { reason }),
  dispute: (id: string, reason: string) => api.post(`/task-batches/${id}/dispute`, { reason }),
  allowManualOverride: (id: string) => api.post(`/task-batches/${id}/allow-manual`),
  updateItemPoints: (id: string, itemPoints: Record<string, number>) =>
    api.put(`/task-batches/${id}/item-points`, { itemPoints }),
  resubmit: (id: string) => api.post(`/task-batches/${id}/resubmit`),
};

export const stageGateAPI = {
  create: (data: { taskId: string; projectId: string; stage: string; gateType: string; mandatory: boolean; assignedToUserId: string; assignedToName: string; notes?: string }) =>
    api.post('/stage-gates', data),
  approve: (id: string, notes?: string) => api.post(`/stage-gates/${id}/approve`, { notes }),
  reject: (id: string, notes?: string) => api.post(`/stage-gates/${id}/reject`, { notes }),
  getTaskGates: (taskId: string) => api.get(`/stage-gates/task/${taskId}`),
  getPending: () => api.get('/stage-gates/pending'),
  getProjectGates: (projectId: string) => api.get(`/stage-gates/project/${projectId}`),
};

export const testCaseAPI = {
  create: (data: { title: string; description?: string; taskId?: string; featureId?: string; projectId: string; stageGateRequestId?: string; steps?: { step: string; expectedResult: string }[]; assignedToUserId: string; assignedToName: string }) =>
    api.post('/test-cases', data),
  update: (id: string, data: { title?: string; description?: string; steps?: { step: string; expectedResult: string }[] }) =>
    api.put(`/test-cases/${id}`, data),
  delete: (id: string) => api.delete(`/test-cases/${id}`),
  execute: (id: string, data: { status: 'passed' | 'failed'; resultNotes?: string }) =>
    api.post(`/test-cases/${id}/execute`, data),
  getByTask: (taskId: string) => api.get(`/test-cases/task/${taskId}`),
  getByFeature: (featureId: string) => api.get(`/test-cases/feature/${featureId}`),
  getMyQueue: (showAll?: boolean) => api.get(`/test-cases/my-queue${showAll ? '?all=true' : ''}`),
  getByProject: (projectId: string) => api.get(`/test-cases/project/${projectId}`),
  getByGate: (gateId: string) => api.get(`/test-cases/gate/${gateId}`),
  reassign: (id: string, data: { assignedToUserId: string; assignedToName: string }) =>
    api.post(`/test-cases/${id}/reassign`, data),
  completeFeatureTesting: (featureId: string, projectId: string) =>
    api.post(`/test-cases/feature/${featureId}/complete`, { projectId }),
};

export const qaTestPlanAPI = {
  createFromFeature: (data: { featureId: string; projectId: string }) =>
    api.post('/qa-test-plans/from-feature', data),
  createFromBug: (data: { bugId: string; projectId: string }) =>
    api.post('/qa-test-plans/from-bug', data),
  createBlank: (data: { projectId: string; name: string }) =>
    api.post('/qa-test-plans/blank', data),
  getProjectPlans: (projectId: string) =>
    api.get(`/qa-test-plans/project/${projectId}`),
  getPlan: (id: string) => api.get(`/qa-test-plans/${id}`),
  updatePlan: (id: string, data: { name?: string; description?: string }) =>
    api.put(`/qa-test-plans/${id}`, data),
  deletePlan: (id: string) => api.delete(`/qa-test-plans/${id}`),
  addItem: (id: string, data: { title: string; description?: string; sourceTaskId?: string }) =>
    api.post(`/qa-test-plans/${id}/items`, data),
  updateItem: (id: string, itemId: string, data: { title?: string; description?: string; status?: string; testNotes?: string }) =>
    api.put(`/qa-test-plans/${id}/items/${itemId}`, data),
  removeItem: (id: string, itemId: string) =>
    api.delete(`/qa-test-plans/${id}/items/${itemId}`),
  submit: (id: string, data: { storyPoints?: number }) =>
    api.post(`/qa-test-plans/${id}/submit`, data),
  approve: (id: string, notes?: string, storyPoints?: number) => api.post(`/qa-test-plans/${id}/approve`, { notes, storyPoints }),
  reject: (id: string, notes?: string) => api.post(`/qa-test-plans/${id}/reject`, { notes }),
  getPendingApprovals: () => api.get('/qa-test-plans/pending-approvals'),
  // Test record history
  getItemHistory: (planId: string, itemId: string) =>
    api.get(`/qa-test-plans/${planId}/items/${itemId}/history`),
  createTestRecord: (planId: string, itemId: string, data: { status: 'passed' | 'failed'; notes?: string; steps?: Array<{ step: string; expectedResult: string; actualResult?: string; passed?: boolean }> }) =>
    api.post(`/qa-test-plans/${planId}/items/${itemId}/test`, data),
  addRecordComment: (recordId: string, text: string) =>
    api.post(`/qa-test-plans/records/${recordId}/comments`, { text }),
};
